import { fg } from '@atlaskit/platform-feature-flags';

import {
	NLPSearchErrorState,
	NLPSearchResultFormat,
	type SearchAIAnswerQueryType,
} from '../../../ai-answer-dialog/types';
import { logger } from '../../../common';
import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';

import {
	type AgentStudioGetAgentsResponse,
	KNOWLEDGE_CARD_AGENT_SEARCH_OPERATION_NAME,
	KnowledgeCardAgentSearchQuery,
} from './gql';

type fetchAgentCardProps = {
	query: string;
	sourceProduct: string;
	limit: number;
	cloudId: string;
	workspaceId: string;
};

const getAgentSearchInput = (query: string): string => {
	// Extract agent name from query if it's in a specific format
	// For now, use the query as-is, but this could be enhanced to parse specific formats
	return query.trim();
};

export const fetchAgentCardData = ({
	query,
	limit,
	sourceProduct,
	cloudId,
	workspaceId,
}: fetchAgentCardProps): Promise<SearchAIAnswerQueryType> => {
	const agentSearchInput = getAgentSearchInput(query);

	return fetchAggResponse<
		{ cloudId: string; input: { name: string }; limit: number },
		AgentStudioGetAgentsResponse
	>({
		graphQuery: KnowledgeCardAgentSearchQuery,
		variables: {
			cloudId,
			input: { name: agentSearchInput },
			limit,
		},
		operationName: KNOWLEDGE_CARD_AGENT_SEARCH_OPERATION_NAME,
		headers: createAtlAttributionHeader({
			atlWorkspaceId: workspaceId,
			tenantId: cloudId,
			product: sourceProduct ?? '',
		}),
	})
		.then((response) => {
			if (response.errors) {
				throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`);
			}

			const edges = response.data?.agentStudio_getAgents?.edges || [];
			const agents = edges.map((edge) => {
				const agent = edge.node;
				return {
					...agent,
					// Normalize agent ID by splitting on "/" and taking the last item
					id: agent.id.split('/').pop(),
					// Replace creator object with just the creator ID
					creator: agent.creator?.id,
					creator_type: agent.creatorType,
					identity_account_id: agent.identityAccountId,
				};
			});

			return {
				nlpSearch: {
					nlpResult: JSON.stringify(agents),
					uniqueSources: [],
					disclaimer: null,
					errorState: null,
					format: NLPSearchResultFormat.JSON,
					...(fg('kd_fix_sain_mau_instrumentation')
						? {
								extraAPIAnalyticsAttributes: {
									requestId: response.extensions?.gateway?.request_id ?? null,
									traceId: response.extensions?.gateway?.trace_id ?? null,
								},
							}
						: {}),
				},
			};
		})
		.catch((error) => {
			if (fg('kd_enable_networkerror_logging')) {
				logger.error({
					error,
					functionName: 'fetchAgentCardData',
				});
			}
			return Promise.resolve({
				nlpSearch: {
					errorState: NLPSearchErrorState.NetworkError,
					disclaimer: null,
					format: NLPSearchResultFormat.JSON,
					uniqueSources: [],
				},
			});
		});
};
